import { useState, type ReactElement } from 'react';

import { UnsavedChangesDialog } from '@/features/patient-record/components/_shared/UnsavedChangesDialog';

import { useDirtyRegistration } from './useDirtyRegistration';

interface DirtyDialogGuardOptions {
  /** Does the form hold unsaved input? */
  readonly dirty: boolean;
  /** Can it be committed now (false ⇒ required field empty)? */
  readonly canSave: boolean;
  /** True while the submit mutation is in flight. */
  readonly saving?: boolean;
  /** Form name for the unsaved-changes prompt + the beforeunload registration. */
  readonly label: string;
  /** Commit the draft (the dialog's submit). */
  readonly onSave: () => void;
  /** Close/discard the dialog. */
  readonly onClose: () => void;
  /**
   * PTV2-1327 — whether the guard registers with the dirty registry. Default `true` (legacy: the
   * guard owns registration). Pass `false` when the dialog already registers via `useEditableDraft`:
   * the guard then only intercepts dismissal + renders the confirm, and does NOT double-register.
   */
  readonly register?: boolean;
}

/** Minimal shape of the radix dismiss events — all we need is preventDefault. */
interface DismissEvent {
  preventDefault: () => void;
}

export interface DirtyDialogGuard {
  /** Spread onto `<Dialog {...rootProps}>` — routes the X/programmatic close through the guard. */
  readonly rootProps: { readonly onOpenChange: (open: boolean) => void };
  /** Spread onto `<DialogContent {...contentProps}>` — guards Escape / outside-click dismissal. */
  readonly contentProps: {
    readonly onEscapeKeyDown: (event: DismissEvent) => void;
    readonly onPointerDownOutside: (event: DismissEvent) => void;
    readonly onInteractOutside: (event: DismissEvent) => void;
  };
  /** Call from an explicit Cancel/Close button so it also gets the guard. */
  readonly requestClose: () => void;
  /** Render this — the unsaved-changes confirm, shown only when a guarded dismiss is pending. */
  readonly confirm: ReactElement | null;
}

/**
 * PTV2-1301 — the shared unsaved-data guard for a create/edit **dialog**. Bundles the three things a
 * dialog needs so each one opts in with a couple of lines instead of re-implementing them
 * (see AddDiagnosisDialog for the hand-written original this replaces):
 *
 *   1. registers with the dirty registry while dirty, so a page unload (refresh / close) is caught
 *      by the guard's beforeunload prompt (a modal blocks section-nav and view-mode, so those can't
 *      reach it — only unload can);
 *   2. intercepts Escape / outside-click / the X so a dirty dialog raises the Save / Discard / Keep
 *      editing prompt instead of vanishing; and
 *   3. renders that prompt, wired to save / discard / keep, with Save disabled + a warning when the
 *      draft isn't committable.
 */
export function useDirtyDialogGuard({
  dirty,
  canSave,
  saving = false,
  label,
  onSave,
  onClose,
  register = true,
}: DirtyDialogGuardOptions): DirtyDialogGuard {
  const [confirmOpen, setConfirmOpen] = useState(false);

  // `active: false` never registers, so register={false} cleanly opts out without a conditional hook.
  useDirtyRegistration({
    active: register && dirty,
    label,
    save: onSave,
    discard: onClose,
    canSave: () => canSave,
  });

  const requestClose = (): void => {
    if (saving) return;
    if (dirty) {
      setConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const guardDismiss = (event: DismissEvent): void => {
    // Keep the dialog open and decide via the confirm, instead of letting radix close (and refocus
    // the content mid-gesture, which selected the form text — see the overlay `select-none` too).
    event.preventDefault();
    requestClose();
  };

  return {
    rootProps: {
      onOpenChange: (open) => {
        if (!open) requestClose();
      },
    },
    contentProps: {
      onEscapeKeyDown: guardDismiss,
      onPointerDownOutside: guardDismiss,
      onInteractOutside: guardDismiss,
    },
    requestClose,
    confirm: confirmOpen ? (
      <UnsavedChangesDialog
        open
        canSave={canSave}
        saving={saving}
        onSave={() => {
          setConfirmOpen(false);
          onSave();
        }}
        onDiscard={() => {
          setConfirmOpen(false);
          onClose();
        }}
        onKeepEditing={() => {
          setConfirmOpen(false);
        }}
      />
    ) : null,
  };
}
